import { useWeb3React } from '@web3-react/core';
import useSWR from 'swr';
import { verifyChainConfig, subgraphQuery } from '../../helpers';
import { EnabledChains, User } from '../../config/constants';
import { formatUnits } from 'ethers/lib/utils';
import useTokenDetails from '../web3/useTokenDetails';
import { config } from '../../config/config';
import { BigNumber } from 'ethers';
import moment from 'moment';

export type PayoutActivity = {
    earnings: string;
    tradeSize: string;
    timestamp: number;
    txHash: string;
};

export type UserReferralList = {
    referralDate: number;
    userAddress: string;
    totalEarnings: string;
    totalVolume: string;
};

const useUserReferralStats = () => {
    const { account, chainId } = useWeb3React();

    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const { data, error } = useSWR(
        account && chainId && decimals
            ? ['userReferralStats', account, chainId, decimals]
            : null,
        async (_, userAddress, chainId, decimals) => {
            verifyChainConfig(chainId);

            const query = `
            user(id: "${userAddress.toLowerCase()}") {
                customReferralFee
                totalReferralFeeAmount
                referralFeeTransfers {
                    amount
                    timestamp
                    volume
                    transactionHash
                    user {
                        id
                    }
                }
                referrals {
                    id
                    referrerRegistrationTimestamp
                    totalReferrerFeeAmount
                    totalReferrerVolume
                }
            }
        `;

            const data: {
                user: Pick<
                    User,
                    | 'referrals'
                    | 'totalReferralFeeAmount'
                    | 'referralFeeTransfers'
                    | 'customReferralFee'
                >;
            } = await subgraphQuery(query, { chainId });

            const payoutActivity: PayoutActivity[] = (
                data.user.referralFeeTransfers || []
            ).map(({ amount, transactionHash, timestamp, volume }) => ({
                earnings: formatUnits(amount, decimals),
                tradeSize: formatUnits(volume || '0', decimals),
                timestamp,
                txHash: transactionHash,
            }));

            const monthtlyReferralStats = (data.user.referralFeeTransfers || [])
                .filter(({ timestamp }) =>
                    moment.unix(timestamp).isAfter(moment().subtract(1, 'month'))
                )
                .reduce(
                    (total, { volume, user }) => {
                        return {
                            ...total,
                            activeUsers: total.activeUsers.includes(user.id)
                                ? total.activeUsers
                                : [...total.activeUsers, user.id],
                            monthlyVolume: total.monthlyVolume.add(volume || '0'),
                        };
                    },
                    { activeUsers: [] as string[], monthlyVolume: BigNumber.from(0) }
                );

            const referralList: UserReferralList[] = data.user.referrals.map(
                ({
                    id,
                    referrerRegistrationTimestamp,
                    totalReferrerFeeAmount,
                    totalReferrerVolume,
                }) => ({
                    referralDate: referrerRegistrationTimestamp!,
                    totalEarnings: formatUnits(totalReferrerFeeAmount, decimals),
                    totalVolume: formatUnits(totalReferrerVolume, decimals),
                    userAddress: id,
                })
            );

            // subgraph returns 0 for default, hence we need a comparison here.
            const defaultFeeBPS = 1_000;
            // BPS =https://www.investopedia.com/ask/answers/what-basis-point-bps/
            const referralFeeBPS = Math.max(
                defaultFeeBPS,
                BigNumber.from(data.user.customReferralFee).toNumber()
            );

            const referralFeeTier = referralFeeBPS * 0.01;

            return {
                referredCount: data.user.referrals.length,
                rewardsEarned: formatUnits(data.user.totalReferralFeeAmount, decimals),
                payoutActivity,
                referralList,
                referralFeeTier,
                monthtlyReferralStats: {
                    activeUsersCount: monthtlyReferralStats.activeUsers.length,
                    monthlyVolume: formatUnits(
                        monthtlyReferralStats.monthlyVolume,
                        decimals
                    ),
                },
            };
        }
    );

    return {
        userReferralStats: data,
        isLoading: !data && !error,
        error,
    };
};

export default useUserReferralStats;
